/**
 * プロフィールのメール連携タブ.
 *
 * - 連携済みアカウント一覧
 * - 新規連携 (Gmail / ...)
 * - 既存連携の「再認証」 (連携解除を伴わずに OAuth 同意画面に再リダイレクト)
 * - OAuth コールバック後の code/state 処理 (EmailOAuthCallbackPage が
 *   sessionStorage に退避した値を拾い、サーバーに POST)
 */

import { useConfirm } from "components/ConfirmDialog";
import { Loading } from "components/ui/Loading/Loading";
import {
  type EmailConnection,
  useDisconnectEmail,
  useEmailConnections,
  useEmailOAuthCallback,
  useStartEmailOAuth,
} from "hooks/use-email-integrations-api";
import type React from "react";
import { useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import EmailConnectionCard from "routes/profile/components/EmailConnectionCard";
import EmailProviderOptionCard from "routes/profile/components/EmailProviderOptionCard";

const PROVIDER_DESCRIPTION: Record<string, string> = {
  gmail:
    "Google ワークスペースのメールを連携し、配信・個別送信への返信を取得します",
  outlook: "Office 365 / Outlook.com のメールを連携します",
};

/**
 * 連携済みプロバイダー集合を返す.
 * @param {EmailConnection[]} connections 連携一覧
 * @return {Set<string>} connected provider names
 */
const connectedProviders = (connections: EmailConnection[]): Set<string> =>
  new Set(connections.map(c => c.provider));

/**
 * プロフィール配下のメール連携タブ.
 * @return {JSX.Element} タブコンテンツ
 */
const EmailIntegrationsTab: React.FC = () => {
  const { connections, loading, refetch } = useEmailConnections();
  const { start, loading: starting } = useStartEmailOAuth();
  const { processCallback, loading: processingCallback } =
    useEmailOAuthCallback();
  const { disconnect, loading: disconnecting } = useDisconnectEmail();
  const { confirm, dialog } = useConfirm();
  // 「再認証」「新規連携」を押下した provider を記憶し、 該当ボタンだけ
  // スピナーを出す (start hook の loading はグローバルなため自前で識別)。
  const [activeOAuthProvider, setActiveOAuthProvider] = useState<string | null>(
    null,
  );

  // EmailOAuthCallbackPage が退避した code/state を処理する.
  // ?tab=integrations 遷移直後に一度だけ走る.
  const callbackProcessed = useRef(false);
  useEffect(() => {
    if (callbackProcessed.current) return;
    const code = sessionStorage.getItem("emailOauthCode");
    const state = sessionStorage.getItem("emailOauthState");
    const provider = sessionStorage.getItem("emailOauthProvider");
    if (!code || !state || !provider) return;
    callbackProcessed.current = true;
    sessionStorage.removeItem("emailOauthCode");
    sessionStorage.removeItem("emailOauthState");
    sessionStorage.removeItem("emailOauthProvider");

    processCallback(provider, code, state)
      .then(conn => {
        toast.success(`${conn.emailAddress} を連携しました`);
        refetch();
      })
      .catch(() => {
        toast.error("連携処理に失敗しました。もう一度お試しください。");
      });
  }, [processCallback, refetch]);

  const handleStart = async (provider: string): Promise<void> => {
    setActiveOAuthProvider(provider);
    try {
      await start(provider);
    } catch {
      toast.error("連携開始に失敗しました。しばらく待って再度お試しください。");
      setActiveOAuthProvider(null);
    }
  };

  const handleDisconnect = async (id: string): Promise<void> => {
    const ok = await confirm({
      title: "連携解除の確認",
      message:
        "この連携を解除します。 過去に取得した返信メールは保持されますが、 今後の自動取り込みは停止します。",
      confirmText: "解除する",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await disconnect(id);
      toast.success("連携を解除しました");
      refetch();
    } catch {
      toast.error("連携解除に失敗しました");
    }
  };

  const handleReauth = async (provider: string): Promise<void> => {
    setActiveOAuthProvider(provider);
    try {
      await start(provider);
    } catch {
      toast.error(
        "再認証の開始に失敗しました。 しばらく待って再度お試しください。",
      );
      setActiveOAuthProvider(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Loading size="lg" />
      </div>
    );
  }

  const connected = connectedProviders(connections);

  return (
    <div className="space-y-8">
      {processingCallback && (
        <div className="flex items-center gap-2 rounded-md bg-sky-50 border border-sky-200 px-3 py-2 text-sm text-sky-800">
          <Loading size="xs" color="#0369a1" />
          連携処理を確定中です…
        </div>
      )}

      <section>
        <h3 className="text-lg font-semibold mb-3">連携中のアカウント</h3>
        {connections.length === 0 ? (
          <div className="text-sm text-gray-500 bg-gray-50 rounded-md p-4">
            まだ連携はありません。下から Gmail 等を連携してください。
          </div>
        ) : (
          <div className="space-y-3">
            {connections.map(c => (
              <EmailConnectionCard
                key={c.id}
                connection={c}
                onDisconnect={handleDisconnect}
                onReauth={handleReauth}
                isDisconnecting={disconnecting}
                isReauthing={starting && activeOAuthProvider === c.provider}
              />
            ))}
          </div>
        )}
      </section>

      <section>
        <h3 className="text-lg font-semibold mb-3">新規連携</h3>
        <div className="space-y-3">
          <EmailProviderOptionCard
            provider="gmail"
            title="Gmail"
            description={PROVIDER_DESCRIPTION.gmail}
            brandColor="#EA4335"
            disabled={connected.has("gmail")}
            comingSoonLabel={connected.has("gmail") ? "連携済み" : undefined}
            onClick={handleStart}
            loading={starting && activeOAuthProvider === "gmail"}
          />
          <EmailProviderOptionCard
            provider="outlook"
            title="Outlook"
            description={PROVIDER_DESCRIPTION.outlook}
            brandColor="#0078D4"
            disabled
          />
        </div>
      </section>

      <section className="bg-gray-50 rounded-md p-4 text-sm text-gray-600 space-y-1">
        <div className="font-medium text-gray-700">連携の目的</div>
        <ul className="list-disc list-inside space-y-1">
          <li>配信・個別送信への返信の自動取得</li>
          <li>案件別・スレッド別の返信追跡</li>
          <li>Sales Board への返信状態表示</li>
        </ul>
      </section>
      {dialog}
    </div>
  );
};

export default EmailIntegrationsTab;
